import { Stack, Typography } from '@mui/material';

import { ApiWorkflowDialog, ArtifactStatusChip } from '@verantos-dev/shared-components';

import { SearchableSelectList } from '../../components/SearchableSelectList';
import { SelectListItem } from '../../components/SelectListItem';
import { useAddCohortPickerModal } from '../../hooks/useAddCohortPickerModal';
import type { AddCohortPickerModalProps } from '../../types';
import { toAssetStatus } from '../../utils/assetStatusLabel';

export const AddCohortPickerModal = ({ projectId, onClose }: AddCohortPickerModalProps) => {
  const {
    title,
    searchPlaceholder,
    emptyMessage,
    noResultsMessage,
    selectHeading,
    searchQuery,
    onSearchChange,
    options,
    selectedId,
    onSelect,
    onConfirm,
    onCancelClose,
    isLoading,
    canConfirm,
    isSubmitting,
    submitError,
    formatSubjectCount,
    dialogMessages,
  } = useAddCohortPickerModal({ projectId, onClose });

  return (
    <ApiWorkflowDialog
      id="addCohortPickerModal"
      title={title}
      dialogMessages={dialogMessages}
      onClose={onCancelClose}
      onSubmit={onConfirm}
      onRetrySubmit={onConfirm}
      isValidForSubmit={canConfirm}
      isSubmitting={isSubmitting}
      submittingError={submitError ? { message: submitError.message } : undefined}
    >
      <Typography variant="body1" sx={{ fontWeight: 500, pt: 1 }}>
        {selectHeading}
      </Typography>
      <SearchableSelectList
        searchPlaceholder={searchPlaceholder}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        noResultsMessage={noResultsMessage}
        testIds={{
          search: 'add-cohort-picker-search',
          loading: 'add-cohort-picker-loading',
          empty: 'add-cohort-picker-empty',
          noResults: 'add-cohort-picker-no-results',
        }}
      >
        {options.map((option) => {
          const effectiveStatus = toAssetStatus(option.latestStatus);
          return (
            <SelectListItem
              key={option.cohortProfileId}
              selected={option.cohortProfileId === selectedId}
              onSelect={() => onSelect(option.cohortProfileId)}
              data-testid={`add-cohort-picker-row-${option.cohortProfileId}`}
            >
              <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                {/* Figma: name = body1_semiBold (16px/600); count = caption (12px). */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                  {option.cohortName}
                </Typography>
                {/* Only a PROCESSED cohort has a real subject count — every
                    other status returns a -1 sentinel, shown as "--" rather
                    than the raw number (matches CohortDetailHeader). */}
                <Typography variant="caption" color="text.secondary">
                  {option.latestSubjectCount !== null && option.latestSubjectCount >= 0
                    ? formatSubjectCount(option.latestSubjectCount)
                    : 'No subjects'}
                </Typography>
              </Stack>
              <ArtifactStatusChip
                status={effectiveStatus}
                data-testid={`add-cohort-picker-status-${option.cohortProfileId}`}
              />
            </SelectListItem>
          );
        })}
      </SearchableSelectList>
    </ApiWorkflowDialog>
  );
};
